import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import BrandLogo from '../components/shared/BrandLogo';
import { BRAND_HERO_GRADIENT } from '../components/shared/SectionTitle';

const LAST_UPDATED = 'February 17, 2026';
const EFFECTIVE_DATE = 'February 17, 2026';

const SECTIONS = [
  { id: 'information-we-collect', title: '1. Information We Collect' },
  { id: 'how-we-use', title: '2. How We Use Your Information' },
  { id: 'information-sharing', title: '3. Information Sharing and Disclosure' },
  { id: 'data-retention', title: '4. Data Retention' },
  { id: 'your-rights', title: '5. Your Privacy Rights' },
  { id: 'security', title: '6. Security' },
  { id: 'international-transfers', title: '7. International Data Transfers' },
  { id: 'children', title: '8. Children\'s Privacy' },
  { id: 'third-party-links', title: '9. Third-Party Services & Revoking Access' },
  { id: 'cookies', title: '10. Cookies and Tracking Technologies' },
  { id: 'changes', title: '11. Changes to This Policy' },
  { id: 'contact', title: '12. Contact Us' },
  { id: 'data-deletion', title: '13. Data Deletion' },
];

const PrivacyPolicyPage = () => {
  const [activeSection, setActiveSection] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);

      const sectionEls = SECTIONS.map(s => document.getElementById(s.id));
      for (let i = sectionEls.length - 1; i >= 0; i--) {
        const el = sectionEls[i];
        if (el && el.getBoundingClientRect().top <= 120) {
          setActiveSection(SECTIONS[i].id);
          break;
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <div className="min-h-screen bg-white dark:bg-dark-950">
      {/* Header */}
      <header className="border-b border-gray-200 dark:border-dark-700 bg-white dark:bg-dark-900 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <BrandLogo size="md" />
          </Link>
          <nav className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">Home</Link>
            <ChevronRightIcon className="w-3 h-3" />
            <span className="text-gray-900 dark:text-white font-medium">Privacy Policy</span>
          </nav>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">
          {/* Sidebar TOC */}
          <aside className="hidden lg:block lg:col-span-1">
            <div className="sticky top-28">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                Table of Contents
              </p>
              <nav className="space-y-1">
                {SECTIONS.map(section => (
                  <a
                    key={section.id}
                    href={`#${section.id}`}
                    className={`block text-sm py-1.5 px-3 rounded-lg transition-colors ${
                      activeSection === section.id
                        ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-600 dark:text-violet-400 font-medium'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-dark-800'
                    }`}
                  >
                    {section.title}
                  </a>
                ))}
              </nav>
              <div className="mt-8 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
                <p className="text-xs font-semibold text-blue-700 dark:text-blue-400 mb-1">Last Updated</p>
                <p className="text-xs text-blue-600 dark:text-blue-300">{LAST_UPDATED}</p>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:col-span-3">
            <div className="mb-10">
              <div className="h-1.5 w-24 rounded-full mb-5" style={{ background: BRAND_HERO_GRADIENT }} aria-hidden />
              <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Privacy Policy</h1>
              <p className="text-gray-600 dark:text-gray-400">
                <strong>Effective Date:</strong> {EFFECTIVE_DATE}
              </p>
              <p className="mt-4 text-gray-700 dark:text-gray-300 leading-relaxed">
                OmniConnX ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we
                collect, use, disclose, and safeguard your information when you use our platform at{' '}
                <span className="font-medium text-violet-600 dark:text-violet-400">omniconnx.com</span> and related
                services (collectively, the "Service"). Please read this policy carefully. By using OmniConnX, you agree to
                the terms described here.
              </p>
            </div>

            <div className="space-y-12 text-gray-700 dark:text-gray-300 leading-relaxed">

              {/* Section 1 */}
              <section id="information-we-collect">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">1. Information We Collect</h2>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">1.1 Information You Provide Directly</h3>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
                  <li><strong>Account Information:</strong> When you register, we collect your name, email address, and password (hashed).</li>
                  <li><strong>Billing Information:</strong> Payment card details are processed by Stripe, Inc. We receive a tokenized payment reference and billing address; we do not store full card numbers.</li>
                  <li><strong>Profile Information:</strong> Optional display name, profile photo, and content niche you provide in Settings.</li>
                  <li><strong>Communications:</strong> Emails or messages you send to our support team.</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">1.2 Platform Integration Data</h3>
                <p className="mb-4">
                  When you connect a social media platform (YouTube, TikTok, Instagram, Twitter/X), we receive an OAuth
                  access token that allows us to retrieve analytics data on your behalf. We collect:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
                  <li>Platform username and user ID (for account identification)</li>
                  <li>Public channel/profile metrics (views, subscribers/followers, engagement rate, impressions)</li>
                  <li>Content performance data (video/post-level metrics you authorize us to access)</li>
                  <li>Revenue and monetization data where the platform API provides it (e.g., YouTube Analytics)</li>
                  <li>OAuth access tokens and refresh tokens (stored encrypted at rest)</li>
                </ul>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-4 mb-6">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Important:</strong> We only request the minimum permissions necessary to provide the Service. We do not
                    post on your behalf, send messages, or access private direct messages without explicit additional consent.
                  </p>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">1.3 Usage and Technical Data</h3>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
                  <li><strong>Log Data:</strong> IP address, browser type, operating system, referring URLs, pages viewed, and timestamps.</li>
                  <li><strong>Device Data:</strong> Device type, screen resolution, and browser language.</li>
                  <li><strong>Usage Data:</strong> Features used, dashboard interactions, filter selections, and session duration to improve product experience.</li>
                  <li><strong>Cookies and Similar Technologies:</strong> Session identifiers, preferences, and analytics (see Section 10).</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">1.4 Data from Third Parties</h3>
                <ul className="list-disc list-outside ml-5 space-y-2">
                  <li>Payment processing information from Stripe (subscription status, invoice history).</li>
                  <li>Fraud detection signals from our security service providers.</li>
                  <li>Single Sign-On data if you register via Google OAuth (name, email, profile picture).</li>
                </ul>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 2 */}
              <section id="how-we-use">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">2. How We Use Your Information</h2>
                <p className="mb-4">We use the information we collect to:</p>
                <div className="space-y-4">
                  {[
                    { title: 'Provide and Operate the Service', desc: 'Authenticate your account, sync platform metrics, generate analytics dashboards, and deliver revenue reconciliation reports.' },
                    { title: 'Billing and Payments', desc: 'Process subscription payments, issue invoices, handle refunds, and prevent payment fraud via Stripe.' },
                    { title: 'Anomaly Detection and Health Monitoring', desc: 'Analyze your metrics trends to detect unusual drops or spikes in engagement, views, or revenue, and alert you proactively.' },
                    { title: 'Personalization', desc: 'Tailor your dashboard, notifications, and AI-powered recommendations based on your goals and content niche.' },
                    { title: 'Communication', desc: 'Send transactional emails (payment receipts, health alerts, platform sync confirmations), account notifications, and — with your consent — product updates and marketing emails.' },
                    { title: 'Legal Compliance', desc: 'Meet obligations under applicable laws (e.g., GDPR, CCPA), respond to lawful government requests, enforce our Terms of Service, and prevent fraud or abuse.' },
                    { title: 'Service Improvement', desc: 'Analyze aggregated, de-identified usage patterns to improve features, fix bugs, and plan the product roadmap. We do not sell individual usage profiles.' },
                    { title: 'Security', desc: 'Detect and prevent unauthorized access, abuse, and other security incidents.' },
                  ].map(item => (
                    <div key={item.title} className="flex gap-3">
                      <div className="mt-1 w-2 h-2 rounded-full bg-violet-500 flex-shrink-0" />
                      <div>
                        <span className="font-semibold text-gray-900 dark:text-white">{item.title}:</span>{' '}
                        {item.desc}
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 3 */}
              <section id="information-sharing">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">3. Information Sharing and Disclosure</h2>
                <p className="mb-4">
                  We do not sell, rent, or trade your personal information to third parties for their marketing purposes.
                  We share information only in the following circumstances:
                </p>
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Service Providers</h3>
                    <p className="mb-3">
                      We engage trusted third-party vendors who process data on our behalf, bound by data processing agreements:
                    </p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border border-gray-200 dark:border-dark-600 rounded-xl overflow-hidden">
                        <thead className="bg-gray-50 dark:bg-dark-800">
                          <tr>
                            <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Provider</th>
                            <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Purpose</th>
                            <th className="text-left p-3 font-semibold text-gray-700 dark:text-gray-300">Data Shared</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 dark:divide-dark-600">
                          {[
                            ['Stripe, Inc.', 'Payment processing', 'Email, billing address, payment token'],
                            ['Amazon Web Services', 'Cloud infrastructure & data storage', 'All platform data (encrypted)'],
                            ['Confluent / Apache Kafka', 'Event streaming infrastructure', 'Anonymized event data'],
                            ['SendGrid / AWS SES', 'Transactional email delivery', 'Email address, notification content'],
                            ['Sentry', 'Error monitoring', 'Anonymized error logs, device/browser info'],
                          ].map(([provider, purpose, data]) => (
                            <tr key={provider} className="bg-white dark:bg-dark-900">
                              <td className="p-3 font-medium text-gray-900 dark:text-white">{provider}</td>
                              <td className="p-3">{purpose}</td>
                              <td className="p-3 text-gray-500 dark:text-gray-400">{data}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Business Transfers</h3>
                    <p>
                      If OmniConnX is involved in a merger, acquisition, or sale of all or part of its assets, your data may be
                      transferred as part of that transaction. We will notify you via email or prominent notice on our Service at
                      least 30 days before any such transfer and give you the opportunity to delete your account.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Legal Requirements</h3>
                    <p>
                      We may disclose information if required by law, court order, or governmental authority, or when we believe
                      disclosure is necessary to protect our rights, prevent fraud, or ensure the safety of our users.
                    </p>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Aggregated / De-identified Data</h3>
                    <p>
                      We may share aggregated, anonymized data (e.g., "creators using our platform see an average 23% improvement
                      in engagement tracking") with partners and in public communications. This data cannot reasonably be used to
                      identify you.
                    </p>
                  </div>
                </div>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 4 */}
              <section id="data-retention">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">4. Data Retention</h2>
                <p className="mb-4">We retain your data only as long as necessary to fulfill the purposes outlined in this policy:</p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-4">
                  <li><strong>Account data:</strong> Retained for the duration of your account plus 30 days after deletion (to handle disputes), then permanently deleted.</li>
                  <li><strong>Platform metrics:</strong> Raw metrics retained for 24 months. Aggregated summaries retained for up to 5 years for trend analysis.</li>
                  <li><strong>Billing records:</strong> Retained for 7 years as required by financial regulations.</li>
                  <li><strong>OAuth tokens:</strong> Deleted immediately upon disconnecting a platform or deleting your account.</li>
                  <li><strong>Support correspondence:</strong> Retained for 2 years.</li>
                  <li><strong>Log data:</strong> Retained for 90 days, then automatically purged.</li>
                </ul>
                <p>
                  To request deletion of your data before the retention period ends, contact us at{' '}
                  <a href="mailto:info@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                    info@omniconnx.com
                  </a>.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 5 */}
              <section id="your-rights">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">5. Your Privacy Rights</h2>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">5.1 Rights for All Users</h3>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
                  <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                  <li><strong>Deletion:</strong> Request deletion of your account and personal data, subject to legal retention obligations.</li>
                  <li><strong>Data Portability:</strong> Receive your data in a machine-readable format (CSV/JSON).</li>
                  <li><strong>Opt-Out of Marketing:</strong> Unsubscribe from marketing emails at any time via the link in each email or in Settings → Notifications.</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">5.2 GDPR Rights (EEA / UK Users)</h3>
                <p className="mb-3">
                  If you are located in the European Economic Area or United Kingdom, you have additional rights under the General
                  Data Protection Regulation (GDPR) or UK GDPR:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
                  <li><strong>Right to Restrict Processing:</strong> Request that we limit how we process your data.</li>
                  <li><strong>Right to Object:</strong> Object to processing based on legitimate interests.</li>
                  <li><strong>Right to Withdraw Consent:</strong> Withdraw consent where processing is based on consent.</li>
                  <li><strong>Right to Lodge a Complaint:</strong> File a complaint with your local data protection authority.</li>
                </ul>
                <p className="mb-6">
                  Our legal bases for processing under GDPR are: (a) performance of a contract (providing the Service), (b) legitimate
                  interests (security, fraud prevention, product improvement), (c) compliance with legal obligations, and (d) your
                  consent (marketing emails, optional analytics cookies).
                </p>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">5.3 CCPA Rights (California Residents)</h3>
                <p className="mb-3">
                  Under the California Consumer Privacy Act (CCPA), California residents have the right to:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-6">
                  <li>Know what personal information is collected, used, shared, or sold.</li>
                  <li>Delete personal information we have collected (with exceptions).</li>
                  <li>Opt-out of the sale or sharing of personal information. <strong>We do not sell personal information.</strong></li>
                  <li>Non-discrimination for exercising your CCPA rights.</li>
                </ul>
                <p>
                  To exercise your rights, contact us at{' '}
                  <a href="mailto:info@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                    info@omniconnx.com
                  </a>{' '}
                  or via Settings → Privacy → Data Requests. We respond within 30 days.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 6 */}
              <section id="security">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">6. Security</h2>
                <p className="mb-4">
                  We implement industry-standard technical and organizational security measures to protect your data:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-4">
                  <li>All data transmitted between your browser and our servers is encrypted using TLS 1.2+.</li>
                  <li>OAuth tokens are encrypted at rest using AES-256 encryption.</li>
                  <li>Passwords are hashed using bcrypt with a minimum cost factor of 12.</li>
                  <li>Our infrastructure runs in ISO 27001-certified data centers on AWS.</li>
                  <li>We conduct regular vulnerability scanning and annual penetration testing.</li>
                  <li>Access to production systems is restricted to authorized personnel and requires multi-factor authentication.</li>
                  <li>We maintain a Security Incident Response Plan with a 72-hour breach notification commitment (in line with GDPR requirements).</li>
                </ul>
                <p>
                  While we strive to protect your data, no method of transmission over the Internet is 100% secure. If you
                  discover a security vulnerability, please report it responsibly to{' '}
                  <a href="mailto:security@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                    security@omniconnx.com
                  </a>.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 7 */}
              <section id="international-transfers">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">7. International Data Transfers</h2>
                <p className="mb-4">
                  OmniConnX is based in the United States. If you access our Service from outside the U.S., your information
                  may be transferred to and processed in the U.S. or other countries where our service providers operate.
                </p>
                <p className="mb-4">
                  For transfers from the EEA or UK to the U.S., we rely on the following safeguards:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-4">
                  <li>Standard Contractual Clauses (SCCs) approved by the European Commission.</li>
                  <li>Data Processing Agreements with all sub-processors incorporating appropriate transfer mechanisms.</li>
                  <li>The EU-U.S. Data Privacy Framework where applicable.</li>
                </ul>
                <p>
                  You may request a copy of the relevant transfer safeguards by contacting{' '}
                  <a href="mailto:info@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                    info@omniconnx.com
                  </a>.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 8 */}
              <section id="children">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">8. Children's Privacy</h2>
                <p className="mb-4">
                  OmniConnX is not directed to children under the age of 13 (or 16 in certain EEA countries). We do not
                  knowingly collect personal information from children. If you believe a child has provided us with personal
                  information, please contact us at{' '}
                  <a href="mailto:info@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                    info@omniconnx.com
                  </a>{' '}
                  and we will promptly delete that information.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 9 */}
              <section id="third-party-links">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">9. Third-Party Services &amp; Revoking Access</h2>
                <p className="mb-4">
                  Our Service may contain links to third-party websites, APIs, and developer consoles (e.g., Google Cloud
                  Console, TikTok Developer Portal). These third-party services have their own privacy policies, and we encourage
                  you to review them. We are not responsible for the privacy practices of third parties.
                </p>
                <p className="mb-4">
                  The data you share with social media platforms (YouTube, TikTok, Instagram, Twitter/X) via OAuth is governed
                  by those platforms' respective privacy policies and terms of service.
                </p>

                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5 mb-4">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Revoking Platform Access</h3>
                  <p className="mb-3">
                    You can disconnect any connected platform at any time in two ways:
                  </p>
                  <ul className="list-disc list-outside ml-5 space-y-2 mb-3">
                    <li>
                      <strong>From OmniConnX:</strong> Go to Settings → Integrations (or the Platforms card on the Analytics dashboard) → click "Disconnect" next to any platform.
                      This immediately deletes your stored OAuth tokens from our systems and stops all data syncing.
                    </li>
                    <li>
                      <strong>Directly from Google:</strong> You can review and revoke OmniConnX's access to your Google/YouTube
                      account at any time by visiting{' '}
                      <a
                        href="https://security.google.com/settings/security/permissions"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-violet-600 dark:text-violet-400 hover:underline font-medium"
                      >
                        Google Account Permissions
                      </a>
                      . Revoking access there will also invalidate any tokens OmniConnX holds.
                    </li>
                    <li>
                      <strong>From other platforms:</strong> TikTok, Instagram, Twitter/X, and Twitch each provide a similar
                      "Connected Apps" or "Authorized Apps" section in their account security settings where you can revoke
                      OmniConnX's access independently.
                    </li>
                  </ul>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Disconnecting a platform removes our access to new data but does not delete historical metrics already
                    collected. To delete that data, submit a deletion request to{' '}
                    <a href="mailto:info@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                      info@omniconnx.com
                    </a>.
                  </p>
                </div>

                <p>
                  OmniConnX's use of information received from Google APIs adheres to the{' '}
                  <a
                    href="https://developers.google.com/terms/api-services-user-data-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-violet-600 dark:text-violet-400 hover:underline"
                  >
                    Google API Services User Data Policy
                  </a>
                  , including the Limited Use requirements.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 10 */}
              <section id="cookies">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">10. Cookies and Tracking Technologies</h2>
                <p className="mb-4">
                  We use cookies and similar tracking technologies. For full details, please see our{' '}
                  <Link to="/cookies" className="text-violet-600 dark:text-violet-400 hover:underline">
                    Cookie Policy
                  </Link>.
                </p>
                <p>
                  In summary, we use: (a) <strong>Essential cookies</strong> required for the Service to function (session
                  management, CSRF protection); (b) <strong>Analytics cookies</strong> (with your consent) to understand how
                  users interact with our product; and (c) <strong>Preference cookies</strong> to remember your settings (dark
                  mode, language). We do not use advertising or tracking cookies for third-party ad targeting.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 11 */}
              <section id="changes">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">11. Changes to This Policy</h2>
                <p className="mb-4">
                  We may update this Privacy Policy periodically. When we make material changes, we will:
                </p>
                <ul className="list-disc list-outside ml-5 space-y-2 mb-4">
                  <li>Update the "Last Updated" date at the top of this page.</li>
                  <li>Send an email notification to registered users at least 14 days before changes take effect.</li>
                  <li>Display a prominent notice on the Service for 30 days after the change.</li>
                </ul>
                <p>
                  Your continued use of the Service after changes become effective constitutes your acceptance of the updated
                  policy. If you do not agree to the updated policy, you may delete your account before the effective date.
                </p>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 12 */}
              <section id="contact">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">12. Contact Us</h2>
                <p className="mb-6">
                  If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please
                  contact our Privacy Team:
                </p>
                <div className="bg-gray-50 dark:bg-dark-800 rounded-2xl p-6 border border-gray-200 dark:border-dark-700">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white mb-2">Privacy Team</p>
                      <p className="text-sm">
                        <a href="mailto:info@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                          info@omniconnx.com
                        </a>
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white mb-2">Security Issues</p>
                      <p className="text-sm">
                        <a href="mailto:security@omniconnx.com" className="text-violet-600 dark:text-violet-400 hover:underline">
                          security@omniconnx.com
                        </a>
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white mb-2">Mailing Address</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        OmniConnX, Inc.<br />
                        Privacy Department<br />
                        548 Market Street, Suite 94103<br />
                        San Francisco, CA 94104<br />
                        United States
                      </p>
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900 dark:text-white mb-2">Response Time</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        We respond to all privacy inquiries within <strong className="text-gray-900 dark:text-white">30 calendar days</strong>.
                        For GDPR requests, we aim to respond within <strong className="text-gray-900 dark:text-white">30 days</strong> as required by law.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              <hr className="border-gray-200 dark:border-dark-700" />

              {/* Section 13 */}
              <section id="data-deletion">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">13. Data Deletion</h2>
                <p className="mb-4">
                  You have the right to request deletion of your data at any time. OmniConnX provides multiple
                  ways to delete your data, including disconnecting individual platforms, deleting your entire
                  account, or submitting a deletion request via email.
                </p>
                <p className="mb-4">
                  For detailed instructions on how to delete your data, including what gets deleted and retention
                  policies, please visit our dedicated{' '}
                  <Link to="/privacy/data-deletion" className="text-violet-600 dark:text-violet-400 hover:underline font-medium">
                    User Data Deletion
                  </Link>{' '}
                  page.
                </p>
                <p>
                  You can also revoke OmniConnX's access to your data directly from each connected platform's
                  security settings (e.g., Facebook Apps and Websites, Google Account Permissions).
                </p>
              </section>

              {/* Footer Links */}
              <div className="pt-4 pb-12 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
                <span>Related:</span>
                <Link to="/terms" className="text-violet-600 dark:text-violet-400 hover:underline">Terms of Service</Link>
                <Link to="/cookies" className="text-violet-600 dark:text-violet-400 hover:underline">Cookie Policy</Link>
                <Link to="/privacy/data-deletion" className="text-violet-600 dark:text-violet-400 hover:underline">Data Deletion</Link>
                <Link to="/contact" className="text-violet-600 dark:text-violet-400 hover:underline">Contact Us</Link>
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* Back to Top */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 bg-gradient-to-r from-pink-500 to-violet-600 hover:from-pink-600 hover:to-violet-700 text-white p-3 rounded-full shadow-lg transition-all z-50"
          aria-label="Back to top"
        >
          <ChevronUpIcon className="w-5 h-5" />
        </button>
      )}
    </div>
  );
};

export default PrivacyPolicyPage;
